import { useEffect, useRef, useState } from "react";
import type { CombinedRow, CombinedRowValue, CountMode, MetricSection, ReportConfig } from "../../types";
import {
    COMBINED_MIN_RULE_SETS,
    COMBINED_ROW_LABEL_MAX_LENGTH,
    addCombinedRuleSet,
    combinedRowLabel,
    combinedRowCountColumns,
    dataColumnSelectionKey,
    combinedRows,
    combinedRuleSetIds,
    combinedRuleSetIssues,
    combinedOtherModelLabels,
    combinedRuleSetOtherModelLabel,
    combinedRuleSetSummary,
    combinedRuleSetUnavailableReason,
    combinedShareExplanation,
    countAndShareRows,
    duplicateCombinedRow,
    extraBreakdownLabel,
    makeCombinedRow,
    moveInList,
    removeCombinedRuleSet,
    ruleSetCategories,
    seedCombinedRows,
    withCombinedRowChange,
    withCombinedRowLabel,
} from "../../reportConfigurationLogic";
import { DestructiveConfirmDialog } from "../DestructiveConfirmDialog";
import { CountModeFields } from "../shared/CountModeFields";
import { SelectPicker } from "../SelectPicker";

// Combined categorizations (Break Down Using -> "Several Rule Sets - Combined Table"): pick the
// rule sets, then the rows. Each rule set is applied on its own over the same rows; each row
// is one category of one rule set, shown as a count or as a share of its rule set. See
// logic/combinedRuleSets.ts for the semantics and Combined-Categorization-Plan.md for the UX.

type CombinedRuleSetsEditorProps = {
    report: ReportConfig;
    section: MetricSection;
    updateMetric: (metricId: string, updater: (metric: MetricSection) => MetricSection) => void;
    // Opens the Categorization tab with the rule set expanded.
    onOpenRuleSet: (ruleSetId: string) => void;
    // A transient message about the last mode switch ("" for none).
    note: string;
    onDismissNote: () => void;
    // Reports a change the user should be told about (e.g. rows added with a rule set).
    onNote: (note: string) => void;
};

type MenuKey = "ruleSets" | "rows" | null;

const VALUE_OPTIONS: { value: CombinedRowValue; label: string }[] = [
    { value: "count", label: "Count" },
    { value: "share", label: "% of rule set" },
];

const ROW_COUNT_OPTIONS: { id: string; value: CountMode | "inherit"; name: string }[] = [
    { id: "inherit", value: "inherit", name: "Use metric setting" },
    { id: "all", value: "all", name: "All records" },
    { id: "distinct", value: "distinct", name: "Distinct Values of a Column" },
];

// <select> option values carry both the rule set id and the category; JSON keeps any
// character in either one (ids may contain ':', categories anything) unambiguous.
/** "Transportation · 2 categories (...)" for a rule set on another data model; the summary alone otherwise. */
function withModelPrefix(modelLabel: string, summary: string) {
    return modelLabel ? `${modelLabel} · ${summary}` : summary;
}

function categoryOptionValue(ruleSetId: string, category: string) {
    return JSON.stringify([ruleSetId, category]);
}

function parseCategoryOptionValue(value: string): [string, string] {
    const parsed = JSON.parse(value) as [string, string];
    return [String(parsed[0] ?? ""), String(parsed[1] ?? "")];
}

function useOutsideClose(open: boolean, close: () => void) {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) close();
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open, close]);
    return ref;
}

export function CombinedRuleSetsEditor({ report, section, updateMetric, onOpenRuleSet, note, onDismissNote, onNote }: CombinedRuleSetsEditorProps) {
    const [menu, setMenu] = useState<MenuKey>(null);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
    const closeMenu = () => setMenu(null);
    const ruleSetMenuRef = useOutsideClose(menu === "ruleSets", closeMenu);
    const rowMenuRef = useOutsideClose(menu === "rows", closeMenu);

    const ids = combinedRuleSetIds(section);
    const rows = combinedRows(section);
    const selected = ids.map((id) => ({ id, ruleSet: report.bucketRuleSets.find((set) => set.id === id) }));
    const otherModelLabels = combinedOtherModelLabels(report, section);
    const issues = combinedRuleSetIssues(report, section);
    const errors = issues.filter((issue) => issue.severity === "error");
    const issueFor = (rowId: string) => issues.filter((issue) => issue.rowId === rowId);
    const timeBreakdowns = section.extraBreakdowns.filter((extra) => extra.granularity);

    const update = (updater: (current: MetricSection) => MetricSection) => updateMetric(section.id, updater);
    const updateRows = (updater: (rows: CombinedRow[]) => CombinedRow[]) => update((current) => ({ ...current, combinedRows: updater(combinedRows(current)) }));
    const updateRow = (rowId: string, updater: (row: CombinedRow) => CombinedRow) => updateRows((current) => current.map((row) => (row.id === rowId ? updater(row) : row)));

    function addRuleSet(ruleSetId: string) {
        const ruleSet = report.bucketRuleSets.find((set) => set.id === ruleSetId);
        // Counted on the rendered section for the message; the update re-applies to the latest one.
        const added = addCombinedRuleSet(report, section, ruleSetId).addedRows;
        update((current) => addCombinedRuleSet(report, current, ruleSetId).section);
        closeMenu();
        if (ruleSet) onNote(added ? `Added "${ruleSet.name || "Untitled Rule Set"}" with ${added} count row${added === 1 ? "" : "s"}. Remove any you don't need.` : `Added "${ruleSet.name || "Untitled Rule Set"}".`);
    }

    function requestRemoveRuleSet(ruleSetId: string) {
        if (rows.some((row) => row.ruleSetId === ruleSetId)) setPendingRemoval(ruleSetId);
        else update((current) => removeCombinedRuleSet(current, ruleSetId));
    }

    function addRows(next: CombinedRow[]) {
        updateRows((current) => [...current, ...next]);
        closeMenu();
    }

    // Category options: one group per selected rule set; a row whose category is no longer
    // produced keeps its value visible in a "No longer produced" group so the select never lies.
    function categoryGroups(row?: CombinedRow) {
        const groups = selected.filter((item) => item.ruleSet).map((item) => ({
            key: item.id,
            label: item.ruleSet!.name || "Untitled Rule Set",
            options: ruleSetCategories(report, item.ruleSet!).categories.map((category) => ({ value: categoryOptionValue(item.id, category), label: category })),
        }));
        if (row && !groups.some((group) => group.options.some((option) => option.value === categoryOptionValue(row.ruleSetId, row.category)))) {
            groups.push({ key: "__missing__", label: "No longer produced", options: [{ value: categoryOptionValue(row.ruleSetId, row.category), label: `${row.category || "(blank)"} (missing)` }] });
        }
        return groups;
    }

    const rowMenuItems = selected.filter((item) => item.ruleSet).flatMap((item) => {
        const categories = ruleSetCategories(report, item.ruleSet!).categories;
        const name = item.ruleSet!.name || "Untitled Rule Set";
        const entries = [{ key: `all-${item.id}`, label: `All categories of ${name}`, hint: categories.length ? `${categories.join(", ")} as counts` : "No fixed categories", disabled: !categories.length, rows: () => seedCombinedRows(report, item.id) }];
        if (categories[0]) entries.push({ key: `pair-${item.id}`, label: `Count and % for ${categories[0]}`, hint: `${name} - the usual SLA pair`, disabled: false, rows: () => countAndShareRows(item.id, categories[0]) });
        return entries;
    });
    const firstUsable = selected.find((item) => item.ruleSet && ruleSetCategories(report, item.ruleSet).categories.length);
    if (firstUsable) {
        rowMenuItems.push({ key: "blank", label: "Add a row", hint: "Pick the category yourself", disabled: false, rows: () => [makeCombinedRow(firstUsable.id, ruleSetCategories(report, firstUsable.ruleSet!).categories[0], "count")] });
    }

    const pendingRuleSet = pendingRemoval ? report.bucketRuleSets.find((set) => set.id === pendingRemoval) : undefined;
    const previewRows = rows;

    return (
        <div className="rc-info-box rc-combined" data-rc-validation={`metric-combined-${section.id}`}>
            {note && (
                <div className="rc-combined-note" role="status">
                    <span className="material-icons md-16" aria-hidden="true">info</span>
                    <span>{note}</span>
                    <button type="button" className="rc-iconbtn muted" aria-label="Dismiss" title="Dismiss" onClick={onDismissNote}>
                        <span className="material-icons md-16" aria-hidden="true">close</span>
                    </button>
                </div>
            )}
            <div className="rc-combined-layout">
                <div className="rc-combined-main">
                    <section className="rc-combined-step" aria-labelledby={`combined-sets-${section.id}`}>
                        <div className="rc-combined-step-head">
                            <div>
                                <p className="rc-lbl" id={`combined-sets-${section.id}`}>1. Rule sets to combine</p>
                                <p className="rc-hint">Each one is applied on its own over the same rows.</p>
                            </div>
                            <div className="rc-combined-menu-anchor" ref={ruleSetMenuRef}>
                                <button type="button" className="rc-minibtn rc-combined-add" aria-haspopup="menu" aria-expanded={menu === "ruleSets"} onClick={() => setMenu(menu === "ruleSets" ? null : "ruleSets")}>
                                    <span className="material-icons md-16" aria-hidden="true">add</span>Add rule set
                                </button>
                                {menu === "ruleSets" && (
                                    <div className="rc-config-select-menu rc-combined-menu" role="menu" aria-label="Rule sets">
                                        {report.bucketRuleSets.length ? report.bucketRuleSets.map((ruleSet) => {
                                            const already = ids.includes(ruleSet.id);
                                            const reason = already ? "Already added" : combinedRuleSetUnavailableReason(report, section, ruleSet);
                                            return (
                                                <button key={ruleSet.id} type="button" role="menuitem" className={reason ? "rc-config-select-option disabled" : "rc-config-select-option"} disabled={!!reason} aria-disabled={!!reason} onClick={() => addRuleSet(ruleSet.id)}>
                                                    <span className="rc-combined-menu-copy">
                                                        <b>{ruleSet.name || "Untitled Rule Set"}</b>
                                                        <small>{reason || withModelPrefix(combinedRuleSetOtherModelLabel(report, section, ruleSet), combinedRuleSetSummary(report, ruleSet))}</small>
                                                    </span>
                                                </button>
                                            );
                                        }) : <p className="rc-hint rc-combined-menu-empty">No rule sets yet. Create them on the Categorization tab.</p>}
                                    </div>
                                )}
                            </div>
                        </div>
                        {selected.length ? (
                            <ol className="rc-combined-sets">
                                {selected.map((item, index) => (
                                    <li className={item.ruleSet ? "rc-combined-set" : "rc-combined-set missing"} key={item.id}>
                                        <span className="rc-detail-ruleset-order">{index + 1}</span>
                                        <span className="rc-detail-ruleset-copy">
                                            <b>{item.ruleSet ? item.ruleSet.name || "Untitled Rule Set" : "Missing rule set"}</b>
                                            <small>{item.ruleSet ? withModelPrefix(combinedRuleSetOtherModelLabel(report, section, item.ruleSet), combinedRuleSetSummary(report, item.ruleSet)) : "This rule set no longer exists. Remove it."}</small>
                                        </span>
                                        {item.ruleSet && <button type="button" className="rc-minibtn" onClick={() => onOpenRuleSet(item.id)}>Open rule set</button>}
                                        <button type="button" className="rc-minibtn" aria-label={`Move ${item.ruleSet?.name || "rule set"} up`} disabled={index === 0} onClick={() => update((current) => ({ ...current, combinedRuleSetIds: moveInList(combinedRuleSetIds(current), index, -1) }))}>Up</button>
                                        <button type="button" className="rc-minibtn" aria-label={`Move ${item.ruleSet?.name || "rule set"} down`} disabled={index === selected.length - 1} onClick={() => update((current) => ({ ...current, combinedRuleSetIds: moveInList(combinedRuleSetIds(current), index, 1) }))}>Down</button>
                                        <button type="button" className="rc-iconbtn muted" aria-label={`Remove ${item.ruleSet?.name || "rule set"}`} title="Remove rule set" onClick={() => requestRemoveRuleSet(item.id)}>
                                            <span className="material-icons md-16" aria-hidden="true">close</span>
                                        </button>
                                    </li>
                                ))}
                            </ol>
                        ) : <p className="rc-hint">No rule sets yet. Add at least {COMBINED_MIN_RULE_SETS}.</p>}
                        <p className="rc-combined-explain">
                            <span className="material-icons md-16" aria-hidden="true">info</span>
                            <span>A record can be counted once in every rule set - a call answered in 20 seconds is both "Within 30s" and "Within 60s" - so rows from different rule sets don't add up to the total.</span>
                        </p>
                        {otherModelLabels.length > 0 && (
                            <p className="rc-combined-explain">
                                <span className="material-icons md-16" aria-hidden="true">info</span>
                                <span>Rule sets from {otherModelLabels.join(", ")} count that data model's own records, placed in periods by its Based On date. Their rows sit in this table next to the others but are not part of the same total.</span>
                            </p>
                        )}
                    </section>

                    <section className="rc-combined-step" aria-labelledby={`combined-rows-${section.id}`}>
                        <div className="rc-combined-step-head">
                            <div>
                                <p className="rc-lbl" id={`combined-rows-${section.id}`}>2. Rows in this table</p>
                                <p className="rc-hint">Pick a category and show it as a count or as a share of its rule set.</p>
                            </div>
                            <div className="rc-combined-menu-anchor" ref={rowMenuRef}>
                                <button type="button" className="rc-minibtn rc-combined-add" aria-haspopup="menu" aria-expanded={menu === "rows"} disabled={!rowMenuItems.length} onClick={() => setMenu(menu === "rows" ? null : "rows")}>
                                    <span className="material-icons md-16" aria-hidden="true">add</span>Add rows
                                </button>
                                {menu === "rows" && (
                                    <div className="rc-config-select-menu rc-combined-menu" role="menu" aria-label="Add rows">
                                        {rowMenuItems.map((item) => (
                                            <button key={item.key} type="button" role="menuitem" className={item.disabled ? "rc-config-select-option disabled" : "rc-config-select-option"} disabled={item.disabled} onClick={() => addRows(item.rows())}>
                                                <span className="rc-combined-menu-copy"><b>{item.label}</b><small>{item.hint}</small></span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {rows.length ? (
                            <div className={section.measure?.fieldId ? "rc-combined-rows" : "rc-combined-rows rc-combined-rows-with-count"} role="table" aria-label="Rows in this table">
                                <div className="rc-combined-row rc-combined-row-head" role="row">
                                    <span role="columnheader">Order</span>
                                    <span role="columnheader">Row label</span>
                                    <span role="columnheader">Category</span>
                                    <span role="columnheader">Show</span>
                                    {!section.measure?.fieldId && <span role="columnheader" className="rc-combined-count-heading">Count</span>}
                                    <span role="columnheader"><span className="rc-sr-only">Actions</span></span>
                                </div>
                                {rows.map((row, index) => {
                                    const rowIssues = issueFor(row.id);
                                    const missingCategory = rowIssues.some((issue) => issue.key.endsWith(".category"));
                                    const duplicateLabel = rowIssues.some((issue) => issue.key.endsWith(".label.duplicate"));
                                    const ruleSet = report.bucketRuleSets.find((set) => set.id === row.ruleSetId);
                                    const fixOptions = ruleSet ? ruleSetCategories(report, ruleSet).categories : [];
                                    const rowCategoryGroups = categoryGroups(row);
                                    return (
                                        <div className={rowIssues.some((issue) => issue.severity === "error") ? "rc-combined-row-wrap has-issue" : "rc-combined-row-wrap"} key={row.id} data-rc-validation={`metric-combined-row-${section.id}-${row.id}`}>
                                            <div className="rc-combined-row" role="row">
                                                <span role="cell" className="rc-combined-order">
                                                    <button type="button" className="rc-iconbtn muted" aria-label={`Move ${combinedRowLabel(row)} up`} disabled={index === 0} onClick={() => updateRows((current) => moveInList(current, index, -1))}><span className="material-icons md-16" aria-hidden="true">arrow_upward</span></button>
                                                    <button type="button" className="rc-iconbtn muted" aria-label={`Move ${combinedRowLabel(row)} down`} disabled={index === rows.length - 1} onClick={() => updateRows((current) => moveInList(current, index, 1))}><span className="material-icons md-16" aria-hidden="true">arrow_downward</span></button>
                                                </span>
                                                <span role="cell" data-label="Row label">
                                                    <input
                                                        aria-label="Row label"
                                                        className={duplicateLabel ? "rc-invalid" : undefined}
                                                        maxLength={COMBINED_ROW_LABEL_MAX_LENGTH}
                                                        value={row.label}
                                                        onChange={(event) => updateRow(row.id, (current) => withCombinedRowLabel(current, event.target.value))}
                                                    />
                                                </span>
                                                <span role="cell" data-label="Category" className={missingCategory ? "rc-invalid" : undefined}>
                                                    <SelectPicker
                                                        ariaLabel="Category"
                                                        value={categoryOptionValue(row.ruleSetId, row.category)}
                                                        options={rowCategoryGroups.flatMap((group) => group.options.map((option) => ({ id: option.value, value: option.value, name: option.label })))}
                                                        optionGroupLabels={Object.fromEntries(rowCategoryGroups.flatMap((group) => group.options.map((option) => [option.value, group.label])))}
                                                        searchPlaceholder="Search Categories..."
                                                        onChange={(value) => {
                                                            const [ruleSetId, category] = parseCategoryOptionValue(value);
                                                            updateRow(row.id, (current) => withCombinedRowChange(current, { ruleSetId, category }));
                                                        }}
                                                    />
                                                </span>
                                                <span role="cell" className="rc-combined-show" data-label="Show">
                                                    <SelectPicker
                                                        ariaLabel="Show as"
                                                        value={row.value}
                                                        options={VALUE_OPTIONS.map((option) => ({ id: option.value, value: option.value, name: option.label }))}
                                                        onChange={(value) => updateRow(row.id, (current) => withCombinedRowChange(current, { value: value as CombinedRowValue }))}
                                                    />
                                                    {row.value === "share" && (
                                                        <span className="material-icons md-16 rc-combined-tip" role="img" aria-label={combinedShareExplanation(report, row)} title={combinedShareExplanation(report, row)}>info</span>
                                                    )}
                                                </span>
                                                {!section.measure?.fieldId && (
                                                    <span role="cell" className="rc-combined-count-fields">
                                                        <CountModeFields
                                                            countMode={row.countMode ?? "inherit"}
                                                            countModeOptions={ROW_COUNT_OPTIONS}
                                                            distinctColumn={row.countDistinctColumn || ""}
                                                            validationField={`metric-combined-count-${section.id}-${row.id}`}
                                                            getColumnOptions={() => combinedRowCountColumns(report, section, row).map((column) => ({ id: dataColumnSelectionKey(column), value: dataColumnSelectionKey(column), name: column.label, sortOrder: column.sortOrder }))}
                                                            onCountModeChange={(value) => updateRow(row.id, (current) => ({ ...current, countMode: value === "inherit" ? undefined : value as CountMode, countDistinctColumn: value === "distinct" ? current.countDistinctColumn : undefined }))}
                                                            onDistinctColumnChange={(value) => updateRow(row.id, (current) => ({ ...current, countDistinctColumn: value }))}
                                                        />
                                                    </span>
                                                )}
                                                <span role="cell" className="rc-combined-actions">
                                                    <button type="button" className="rc-iconbtn muted" aria-label={`Duplicate ${combinedRowLabel(row)} as ${row.value === "share" ? "a count" : "a share"}`} title={row.value === "share" ? "Duplicate as a count" : "Duplicate as a %"} onClick={() => update((current) => duplicateCombinedRow(current, row.id))}><span className="material-icons md-16" aria-hidden="true">content_copy</span></button>
                                                    <button type="button" className="rc-iconbtn muted" aria-label={`Remove ${combinedRowLabel(row)}`} title="Remove row" onClick={() => updateRows((current) => current.filter((item) => item.id !== row.id))}><span className="material-icons md-16" aria-hidden="true">delete</span></button>
                                                </span>
                                            </div>
                                            {row.value === "share" && <p className="rc-combined-row-note">{combinedShareExplanation(report, row)}</p>}
                                            {missingCategory && (
                                                <div className="rc-combined-row-fix">
                                                    <span>"{row.category}" is no longer produced by {ruleSet?.name || "its rule set"}.</span>
                                                    {fixOptions.length > 0 && (
                                                        <label>
                                                            Replace with
                                                            <select value="" onChange={(event) => { const value = event.target.value; if (value) updateRow(row.id, (current) => withCombinedRowChange(current, { category: value })); }}>
                                                                <option value="">Choose...</option>
                                                                {fixOptions.map((category) => <option key={category} value={category}>{category}</option>)}
                                                            </select>
                                                        </label>
                                                    )}
                                                </div>
                                            )}
                                            {rowIssues.filter((issue) => !issue.key.endsWith(".category")).map((issue) => (
                                                <p key={issue.key} className={issue.severity === "error" ? "rc-combined-row-issue" : "rc-combined-row-issue warning"}>{issue.message}</p>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : <p className="rc-hint">No rows yet. Use "Add rows" to add categories from the rule sets above.</p>}
                    </section>

                    <p className="rc-hint">
                        {timeBreakdowns.length
                            ? <>Columns: {timeBreakdowns.map((extra) => extraBreakdownLabel(section, extra)).join(", ")} (set under "Also Break Down By" below).</>
                            : <>Tip: add a date breakdown below (for example the call date by month) to get one column per period.</>}
                    </p>
                </div>

                <aside className="rc-combined-side" aria-label="Table structure and checks">
                    <div className="rc-combined-panel">
                        <div className="rc-combined-panel-head"><b>Table structure</b><span className="rc-badge">No query run</span></div>
                        {previewRows.length ? (
                            <div className="rc-combined-preview-wrap">
                                <table className="rc-combined-preview">
                                    <thead>
                                        <tr><th scope="col">{section.excelLayout?.columns.find((column) => column.role === "rowLabel")?.rowLabel?.displayLabel || "Category"}</th><th scope="col">{timeBreakdowns.length ? "Period 1" : "Value"}</th>{timeBreakdowns.length > 0 && <th scope="col">...</th>}<th scope="col">Total</th></tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row) => (
                                            <tr key={row.id}>
                                                <td>{combinedRowLabel(row)}</td>
                                                <td>{row.value === "share" ? "%" : "#"}</td>
                                                {timeBreakdowns.length > 0 && <td>...</td>}
                                                <td className="rc-combined-total">{row.value === "share" ? "Recalculated %" : "Sum"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : <p className="rc-hint">Rows you add appear here.</p>}
                        <p className="rc-hint">Counts are summed; % rows are recalculated from the totals, never averaged.</p>
                    </div>
                    <div className="rc-combined-panel">
                        <div className="rc-combined-panel-head"><b>Checks</b></div>
                        {errors.length === 0 ? (
                            <p className="rc-combined-ok"><span className="material-icons md-16" aria-hidden="true">check_circle</span>Ready. The query and the workbook use these rows.</p>
                        ) : (
                            <ul className="rc-combined-issues">
                                {errors.filter((issue) => !issue.rowId).map((issue) => <li key={issue.key}>{issue.message}</li>)}
                                {errors.some((issue) => issue.rowId) && <li>{errors.filter((issue) => issue.rowId).length} row problem{errors.filter((issue) => issue.rowId).length === 1 ? "" : "s"} - see the highlighted rows.</li>}
                            </ul>
                        )}
                    </div>
                </aside>
            </div>

            <DestructiveConfirmDialog
                open={!!pendingRemoval}
                kind="report"
                title="Remove Rule Set"
                kicker="Combined table"
                description="These rows use the rule set and will be removed with it."
                subject={pendingRuleSet?.name || "Untitled Rule Set"}
                subjectIcon="category"
                visualIcon="playlist_remove"
                impact={rows.filter((row) => row.ruleSetId === pendingRemoval).map(combinedRowLabel)}
                confirmLabel="Remove rule set and rows"
                onCancel={() => setPendingRemoval(null)}
                onConfirm={() => {
                    if (pendingRemoval) update((current) => removeCombinedRuleSet(current, pendingRemoval));
                    setPendingRemoval(null);
                }}
            />
        </div>
    );
}
